import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiShare2, FiPrinter, FiHeart } from 'react-icons/fi';
import { FaTruck, FaRuler, FaWeightHanging, FaToolbox, FaDollarSign, FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaCheckCircle, FaShieldAlt, FaClipboardList } from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';
import { vehicleService } from '../services/vehicleService';
import { formatVehicleTitle } from '../utils/textFormatting';
import { canonicalizeUrl } from '../utils/canonicalUrl';
import { sampleTrailers } from '../data/sampleTrailers';
import ImageGallery from '../components/ImageGallery';
import LeadForm from '../components/LeadForm';

const TrailerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [trailer, setTrailer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [inquiryType, setInquiryType] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    loadTrailer();
  }, [id]);

  const loadTrailer = async () => {
    if (!id) {
      console.error('TrailerDetail: No ID provided');
      setError('No trailer ID provided');
      setLoading(false);
      return;
    }
    
    console.log('TrailerDetail: Loading trailer with ID:', id);
    
    try {
      setLoading(true);
      setError('');
      
      // First try to get it as a regular vehicle
      const result = await vehicleService.getVehicleById(id);
      console.log('TrailerDetail: Raw result from vehicleService:', result);
      
      // Handle both possible response formats
      const data = result?.data || result;
      const error = result?.error;
      
      if (data && !error) {
        console.log('TrailerDetail: Successfully loaded trailer:', data);
        setTrailer(data);
      } else {
        console.error('TrailerDetail: No trailer data found for ID:', id, 'Error:', error);
        // Try getting all trailers and finding this one
        const allTrailers = await vehicleService.getVehiclesByType(['trailer', 'trailers']);
        console.log('TrailerDetail: Fetched all trailers:', allTrailers);
        
        if (allTrailers?.data && allTrailers.data.length > 0) {
          const found = allTrailers.data.find(t => t.id === id || t.id === parseInt(id));
          if (found) {
            console.log('TrailerDetail: Found trailer in all trailers list:', found);
            setTrailer(found);
          } else {
            console.error('TrailerDetail: Trailer not found in database, using sample data');
            // Fall back to sample data
            const sampleTrailer = sampleTrailers.find(t => t.id === parseInt(id));
            if (sampleTrailer) {
              console.log('TrailerDetail: Using sample trailer:', sampleTrailer);
              setTrailer(sampleTrailer);
            } else {
              setError('Trailer not found');
            }
          }
        } else {
          console.log('TrailerDetail: No trailers in database, using sample data');
          // Fall back to sample data
          const sampleTrailer = sampleTrailers.find(t => t.id === parseInt(id));
          if (sampleTrailer) {
            console.log('TrailerDetail: Using sample trailer:', sampleTrailer);
            setTrailer(sampleTrailer);
          } else {
            setError('Trailer not found');
          }
        }
      }
    } catch (err) {
      console.error('TrailerDetail: Error loading trailer:', err);
      // Fall back to sample data on error
      const sampleTrailer = sampleTrailers.find(t => t.id === parseInt(id));
      if (sampleTrailer) {
        console.log('TrailerDetail: Using sample trailer after error:', sampleTrailer);
        setTrailer(sampleTrailer);
      } else {
        setError('Failed to load trailer details');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInquiry = (type) => {
    setInquiryType(type);
    setShowLeadForm(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !trailer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{error || 'Trailer not found'}</h2>
          <Link to="/trailers" className="text-primary-600 hover:text-primary-700">
            ← Back to Trailers
          </Link>
        </div>
      </div>
    );
  }

  if (!trailer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Loading trailer...</h2>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${trailer?.year || ''} ${trailer?.make || ''} ${trailer?.model || 'Trailer'} for Sale in Paradise, TX | Paradise Performance LLC`}</title>
        <meta name="description" content={`Buy this ${trailer?.year || ''} ${trailer?.make || ''} ${trailer?.model || ''} trailer for $${trailer?.price?.toLocaleString() || 'Call for price'}. Length: ${trailer?.length || 'N/A'}ft, Capacity: ${trailer?.capacity || 'N/A'}lbs. Quality trailers in Paradise, TX. Financing available.`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="product" />
        <meta property="og:url" content={canonicalizeUrl(`/trailer/${trailer?.id || id}`)} />
        <meta property="og:title" content={`${trailer?.year || ''} ${trailer?.make || ''} ${trailer?.model || 'Trailer'} - $${trailer?.price?.toLocaleString() || 'Call for price'}`} />
        <meta property="og:description" content={`${trailer?.length || 'N/A'}ft • ${trailer?.capacity || 'N/A'}lbs capacity • ${trailer?.condition || 'Used'}`} />
        <meta property="og:image" content={trailer?.images?.[0] || trailer?.image || ''} />
        <meta property="product:price:amount" content={trailer?.price || 0} />
        <meta property="product:price:currency" content="USD" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={`${trailer?.year || ''} ${trailer?.make || ''} ${trailer?.model || 'Trailer'}`} />
        <meta property="twitter:description" content={`For Sale: $${trailer?.price?.toLocaleString() || 'Call'} • ${trailer?.length || 'N/A'}ft • ${trailer?.capacity || 'N/A'}lbs`} />
        <meta property="twitter:image" content={trailer?.images?.[0] || trailer?.image || ''} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={canonicalizeUrl(`/trailer/${trailer?.id || id}`)} />
        
        {/* Structured Data - Product Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": `${trailer?.year || ''} ${trailer?.make || ''} ${trailer?.model || 'Trailer'}`,
            "brand": {
              "@type": "Brand",
              "name": trailer?.make || "Trailer"
            },
            "model": trailer?.model || '',
            "description": `${trailer?.length || 'N/A'}ft ${trailer?.type || 'utility'} trailer with ${trailer?.capacity || 'N/A'}lbs capacity`,
            "offers": {
              "@type": "Offer",
              "priceCurrency": "USD",
              "price": trailer?.price || 0,
              "availability": trailer?.available ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
              "seller": {
                "@type": "AutoDealer",
                "name": "Paradise Performance LLC",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "305 Co Rd 3470",
                  "addressLocality": "Paradise",
                  "addressRegion": "TX",
                  "postalCode": "76073"
                },
                "telephone": "(817) 382-2316"
              }
            },
            "image": trailer?.images?.length > 0 ? trailer?.images : [trailer?.image || '']
          })}
        </script>
      </Helmet>
      <div className="min-h-screen bg-gray-50 pt-20">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <nav className="flex items-center space-x-2 text-sm">
                <Link to="/" className="text-gray-500 hover:text-primary-600">Home</Link>
                <span className="text-gray-400">/</span>
                <Link to="/trailers" className="text-gray-500 hover:text-primary-600">Trailers</Link>
                <span className="text-gray-400">/</span>
                <span className="text-gray-900">{trailer.year || ''} {trailer.make || ''} {trailer.model || ''}</span>
              </nav>
              <div className="flex items-center space-x-2">
                <button className="p-2 text-gray-600 hover:text-primary-600">
                  <SafeIcon icon={FiShare2} className="w-5 h-5" />
                </button>
                <button className="p-2 text-gray-600 hover:text-primary-600">
                  <SafeIcon icon={FiPrinter} className="w-5 h-5" />
                </button>
                <button className="p-2 text-gray-600 hover:text-primary-600">
                  <SafeIcon icon={FiHeart} className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Gallery */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-lg shadow-lg overflow-hidden"
              >
                <ImageGallery 
                  images={Array.isArray(trailer.images) ? trailer.images : []}
                  videos={Array.isArray(trailer.videos) ? trailer.videos : []}
                  alt={`${trailer.year || ''} ${trailer.make || ''} ${trailer.model || ''}`}
                />
              </motion.div>

              {/* Trailer Header */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-lg shadow-lg p-6"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                      {formatVehicleTitle(trailer)}
                    </h1>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      {trailer.stockNumber && (
                        <span>Stock #: {trailer.stockNumber}</span>
                      )}
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        trailer.condition === 'new' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {trailer.condition || 'Used'}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-primary-600">
                      ${trailer.price?.toLocaleString() || '0'}
                    </div>
                  </div>
                </div>

                {/* Key Specs */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
                  {trailer.length && (
                    <div className="text-center">
                      <FaRuler className="w-8 h-8 mx-auto mb-2 text-primary-600" />
                      <div className="text-sm text-gray-600">Length</div>
                      <div className="font-semibold">{trailer.length}'</div>
                    </div>
                  )}
                  {trailer.width && (
                    <div className="text-center">
                      <FaRuler className="w-8 h-8 mx-auto mb-2 text-primary-600" />
                      <div className="text-sm text-gray-600">Width</div>
                      <div className="font-semibold">{trailer.width}'</div>
                    </div>
                  )}
                  {trailer.capacity && (
                    <div className="text-center">
                      <FaWeightHanging className="w-8 h-8 mx-auto mb-2 text-primary-600" />
                      <div className="text-sm text-gray-600">Capacity</div>
                      <div className="font-semibold">{trailer.capacity} lbs</div>
                    </div>
                  )}
                  {trailer.axles && (
                    <div className="text-center">
                      <FaTruck className="w-8 h-8 mx-auto mb-2 text-primary-600" />
                      <div className="text-sm text-gray-600">Axles</div>
                      <div className="font-semibold">{trailer.axles}</div>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Tabs Content */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-lg shadow-lg"
              >
                <div className="border-b">
                  <div className="flex space-x-8 px-6">
                    <button
                      onClick={() => setActiveTab('overview')}
                      className={`py-4 border-b-2 font-medium transition-colors ${
                        activeTab === 'overview'
                          ? 'border-primary-600 text-primary-600'
                          : 'border-transparent text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Overview
                    </button>
                    <button
                      onClick={() => setActiveTab('specifications')}
                      className={`py-4 border-b-2 font-medium transition-colors ${
                        activeTab === 'specifications'
                          ? 'border-primary-600 text-primary-600'
                          : 'border-transparent text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Specifications
                    </button>
                    {trailer.features && trailer.features.length > 0 && (
                      <button
                        onClick={() => setActiveTab('features')}
                        className={`py-4 border-b-2 font-medium transition-colors ${
                          activeTab === 'features'
                            ? 'border-primary-600 text-primary-600'
                            : 'border-transparent text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        Features
                      </button>
                    )}
                  </div>
                </div>

                <div className="p-6">
                  {activeTab === 'overview' && (
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-4">Trailer Overview</h3>
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {trailer.description || `This ${trailer.year} ${trailer.make} ${trailer.model} trailer is available at Paradise Performance LLC. Contact us for more information about this quality trailer.`}
                      </p>
                    </div>
                  )}

                  {activeTab === 'specifications' && (
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-4">Specifications</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-3">
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-gray-600">Type</span>
                            <span className="font-medium">{trailer.trailerType || 'Trailer'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-gray-600">Make</span>
                            <span className="font-medium">{trailer.make}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-gray-600">Model</span>
                            <span className="font-medium">{trailer.model}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-gray-600">Year</span>
                            <span className="font-medium">{trailer.year}</span>
                          </div>
                          {trailer.exteriorColor && (
                            <div className="flex justify-between py-2 border-b">
                              <span className="text-gray-600">Color</span>
                              <span className="font-medium">{trailer.exteriorColor}</span>
                            </div>
                          )}
                        </div>
                        <div className="space-y-3">
                          {trailer.length && (
                            <div className="flex justify-between py-2 border-b">
                              <span className="text-gray-600">Length</span>
                              <span className="font-medium">{trailer.length} feet</span>
                            </div>
                          )}
                          {trailer.width && (
                            <div className="flex justify-between py-2 border-b">
                              <span className="text-gray-600">Width</span>
                              <span className="font-medium">{trailer.width} feet</span>
                            </div>
                          )}
                          {trailer.weight && (
                            <div className="flex justify-between py-2 border-b">
                              <span className="text-gray-600">Weight</span>
                              <span className="font-medium">{trailer.weight} lbs</span>
                            </div>
                          )}
                          {trailer.capacity && (
                            <div className="flex justify-between py-2 border-b">
                              <span className="text-gray-600">Load Capacity</span>
                              <span className="font-medium">{trailer.capacity} lbs</span>
                            </div>
                          )}
                          {trailer.axles && (
                            <div className="flex justify-between py-2 border-b">
                              <span className="text-gray-600">Axles</span>
                              <span className="font-medium">{trailer.axles}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'features' && trailer.features && (
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-4">Features</h3>
                      <div className="grid md:grid-cols-2 gap-3">
                        {trailer.features.map((feature, index) => (
                          <div key={index} className="flex items-center space-x-2">
                            <FaCheckCircle className="text-green-600 flex-shrink-0" />
                            <span className="text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              {/* Pricing Card */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white rounded-lg shadow-lg p-6 sticky top-24"
              >
                <div className="text-center mb-6">
                  <div className="text-3xl font-bold text-primary-600 mb-2">
                    ${trailer.price?.toLocaleString() || '0'}
                  </div>
                  <p className="text-sm text-gray-600">Plus taxes & fees</p>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => handleInquiry('viewing')}
                    className="w-full border border-gray-300 py-3 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <FaClipboardList className="inline mr-2" />
                    Schedule Viewing
                  </button>
                  <button
                    className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-semibold transition-colors"
                  >
                    <FaDollarSign className="inline mr-2" />
                    Purchase Now
                  </button>
                  <button
                    onClick={() => handleInquiry('inquiry')}
                    className="w-full border border-gray-400 text-gray-700 hover:bg-gray-50 py-3 rounded-lg font-semibold transition-colors"
                  >
                    <FaEnvelope className="inline mr-2" />
                    Contact Us
                  </button>
                  <a
                    href="tel:8173822316"
                    className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-semibold transition-colors flex items-center justify-center"
                  >
                    <FaPhoneAlt className="mr-2" />
                    Call (817) 382-2316
                  </a>
                </div>

                <div className="mt-6 pt-6 border-t">
                  <h4 className="font-semibold text-gray-900 mb-3">Contact Information</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-600">
                      <FaMapMarkerAlt className="mr-2 text-primary-600" />
                      305 Co Rd 3470, Paradise, TX
                    </div>
                    <div className="flex items-center text-gray-600">
                      <FaPhoneAlt className="mr-2 text-primary-600" />
                      (817) 382-2316
                    </div>
                    <div className="flex items-center text-gray-600">
                      <FaEnvelope className="mr-2 text-primary-600" />
                      service@paradise-performance.com
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t">
                  <div className="flex items-center text-sm text-gray-600 mb-2">
                    <FaShieldAlt className="mr-2 text-primary-600" />
                    Quality Guaranteed
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <FaToolbox className="mr-2 text-primary-600" />
                    Service Available
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Lead Form Modal */}
      {showLeadForm && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
              <h2 className="text-xl font-bold">
                {inquiryType === 'viewing' ? 'Schedule Viewing' : 
                 inquiryType === 'test-drive' ? 'Schedule Test Drive' : 
                 'Trailer Inquiry'}
              </h2>
              <button
                onClick={() => setShowLeadForm(false)}
                className="text-gray-500 hover:text-gray-700 text-3xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="p-6">
              <LeadForm
                vehicleInfo={{
                  year: trailer?.year || new Date().getFullYear(),
                  make: trailer?.make || 'Trailer',
                  model: trailer?.model || '',
                  vin: trailer?.vin || trailer?.id || 'N/A',
                  price: trailer?.price || 0,
                  type: 'trailer'
                }}
                leadType={inquiryType === 'viewing' ? 'test_drive' : 'inquiry'}
                onClose={() => setShowLeadForm(false)}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TrailerDetail;
